<template>
  <div>
    <ForumHeader :has-notice="hasNotice" />
    <div class="forum-homepage">
      <div class="forum-content">
        <div>
          <h1>
            {{ user && user.name }}
          </h1>
        </div>
        <div>
          <Spin
            v-if="loading"
            fix
          >
            <i
              class="fas fa-spinner fa-spin"
              style="font-size: 40px; color: var(--color-green-thick);"
            />
            <p>Loading</p>
          </Spin>
          <template v-else>
            <List
              class="thread-list"
              :style="listCssStyle"
            >
              <PostThread
                v-for="post in postsForPage"
                :key="post.id"
                :page-view-switch="pageViewSwitch"
                :thread-data="post"
                class="thread-list-content"
                @click.native="openPostDetail(post)"
              />
              <div
                slot="header"
                class="thread-list-header"
              >
                <div class="thread-heading">
                  Post
                </div>
                <div class="thread-reply">
                  Replies
                </div>
                <div class="thread-like">
                  Likes
                </div>
                <div class="thread-last-updated">
                  Last Reply
                </div>
              </div>
              <Page
                slot="footer"
                :current="currentPage"
                :total="posts.length"
                :page-size="pageSize"
                show-elevator
                @on-change="onPageChange"
              />
            </List>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import PostThread from './helper-components/forum/PostThread'
import ForumHeader from './helper-components/forum/ForumHeader'
import { mapState } from 'vuex'

export default {
  components: {
    ForumHeader,
    PostThread
  },

  props: {
    hasNotice: Boolean
  },

  data () {
    return {
      pageViewSwitch: false,
      currentPage: 1,
      loading: true,
      posts: []
    }
  },

  computed: {
    ...mapState('user', {
      users: 'allUsers'
    }),

    ...mapState('forum', {
      pageSize: 'pageSize',
      postsTags: 'postsTagsMap',
      postsReplyNumber: 'postsReplyNumberMap',
      threads: 'threads',
      filteredThreads: 'filteredThreads',
      likesForPage: 'likesForPage',
      postsMap: 'postsMap'
    }),

    postsForPage () {
      const startIndex = (this.currentPage - 1) * this.pageSize
      this.pageViewSwitch = !this.pageViewSwitch
      return this.posts.slice(startIndex, startIndex + this.pageSize)
    },

    listCssStyle () {
      const colWidth = 64
      const colMargin = 8
      const lastReplyWidth = 176
      const totalColumns = 2
      return {
        '--col-width': `${colWidth}px`,
        '--col-margin': `${colMargin}px`,
        '--last-reply-width': `${lastReplyWidth}px`,
        '--title-width': `calc(100% - ${(totalColumns * (colWidth + colMargin)) + lastReplyWidth}px)`
      }
    },

    user () {
      return this.users.find(user => user.id === Number(this.$route.query.userId))
    }
  },

  async mounted () {
    this.loading = true
    try {
      await this.$store.dispatch('forum/fetchPostsForUser', { userId: this.$route.query.userId })

      this.posts = Object.values(this.postsMap).map(post => {
        const { id, title, authorId, createdAt, updatedAt, lastReplyAt, replierId, content, sticky } = post
        const author = this.users.find(user => user.id === authorId)
        const lastReplier = this.users.find(user => user.id === replierId)
        const numReplies = (this.postsReplyNumber && this.postsReplyNumber[id]) ? this.postsReplyNumber[id] : 0
        const lastUpdated = updatedAt
        const tags = (this.postsTags && this.postsTags[id]) ? this.postsTags[id].map(item => item.name) : []

        return {
          id,
          title,
          content,
          createdAt,
          lastUpdated,
          lastReplyAt,
          author,
          lastReplier,
          numReplies,
          tags,
          sticky: sticky === 1
        }
      }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    } catch (error) {
      console.error(error)
      this.$Message.error(`Could not forum posts for users: ${error}`)
    }

    this.loading = false
  },

  methods: {
    /**
     * Event handler for when the page changes
     * @param {String} pageNumber - The new page number
     */
    onPageChange (pageNumber) {
      this.currentPage = pageNumber
    },

    /**
     * Route to post detail page after click
     * @param {Object} thread
     */
    openPostDetail (thread) {
      this.$router.push({
        name: 'Forum Post',
        params: {
          id: thread.id,
          postData: thread
        }
      })
    }
  }
}
</script>
<style scoped>
  .forum-content {
    margin: 0 auto;
    padding: 32px 8px;
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .thread-list-wrapper {
    margin: 0;
    padding: 8px 0;
    width: 100%;
    height: 100%;
    position: relative;
    min-height: 150px;
    display: flex;
    justify-content: center;
    background-color: white;
    border-radius: 10px;
  }

  .thread-list-wrapper >>> .ivu-spin-fix {
    color: var(--color-green-thick);
    background-color: transparent;
  }

  .thread-list-wrapper >>> .ivu-spin-fix p {
    margin-top: 8px;
    font-weight: bold;
  }

  .thread-list {
    width: 100%;
  }

  .thread-list >>> .ivu-list-header {
    padding: 0;
  }

  .thread-list-header {
    padding: 8px 16px;
    display: flex;
    flex-direction: row;
    font-size: 14px;
    font-weight: bold;
    color: var(--color-text-light);
  }

  .thread-heading {
    display: flex;
    flex-direction: row;
    align-items: top;
    width: var(--title-width);
  }

  .thread-sticky,
  .thread-reply,
  .thread-like {
    margin-right: var(--col-margin);
    width: var(--col-width);
    text-align: center;
  }

  .thread-last-updated {
    width: var(--last-reply-width);
  }

  @media screen and (min-width: 1024px) {
    .forum-content {
      max-width: 1024px;
    }
  }

  @media screen and (min-width: 1440px) {
    .forum-content {
      padding: 32px 64px;
      max-width: 1440px;
    }
  }

  .tag-list {
    margin-bottom: 16px;
  }

  .post-tag {
    background-color: transparent;
    cursor: pointer;
  }

  .post-tag >>> .ivu-tag-text {
    color: var(--color-green-thick);
  }
</style>
